import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import { Box, IconButton, Snackbar } from "@mui/material";
import styled from "styled-components";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const StyledSnackbar = styled.div`
  background-color: #ededed;
  padding: 8px 12px;
  font-size: 13px;
  max-width: 360px;
  min-width: 200px;
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
`;

const StyledErrorSnackbar = styled(StyledSnackbar)`
  border: 1px solid #e91411;
  color: #e91411;
  padding: 6px 4px 6px 12px;
`;

const StyledSuccessSnackbar = styled(StyledSnackbar)`
  border: 1px solid #37b400;
  color: #37b400;
`;

interface SnackbarContextType {
  showSnackbar: (_message: string, _isError?: boolean) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined
);

export const successSnackbarCloseTime = 6000;

interface SnackbarProviderProps {
  children: ReactNode;
  shouldSuppressErrorSnackbar?: () => boolean;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({
  children,
  shouldSuppressErrorSnackbar,
}) => {
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);
  const [open, setOpen] = useState<boolean>(false);

  const showSnackbar = useCallback(
    (message: string, isError?: boolean): void => {
      if (isError && shouldSuppressErrorSnackbar?.()) {
        return;
      }

      setIsError(!!isError);
      setSnackbarMessage(message);
      setOpen(true);
    },
    [shouldSuppressErrorSnackbar]
  );

  const snackbarContextValue = useMemo(
    () => ({ showSnackbar }),
    [showSnackbar]
  );

  const handleClose = (
    _event: React.SyntheticEvent | Event,
    reason?: string
  ): void => {
    if (reason !== "clickaway") {
      setOpen(false);
    }
  };

  return (
    <SnackbarContext.Provider value={snackbarContextValue}>
      {children}
      {snackbarMessage && (
        <Snackbar
          open={open}
          autoHideDuration={isError ? null : successSnackbarCloseTime}
          onClose={handleClose}
          TransitionProps={{
            onExited: () => setSnackbarMessage(null),
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          aria-label="Snackbar container"
        >
          <Box sx={{ boxShadow: 3 }}>
            {isError ? (
              <StyledErrorSnackbar aria-label="Error snackbar container">
                <span>{snackbarMessage}</span>
                <IconButton
                  onClick={handleClose}
                  aria-label="Snackbar close button"
                >
                  <CancelIcon sx={{ color: "#e91411" }} />
                </IconButton>
              </StyledErrorSnackbar>
            ) : (
              <StyledSuccessSnackbar aria-label="Success snackbar container">
                <span>{snackbarMessage}</span>
                <CheckCircleIcon sx={{ color: "#37b400" }} />
              </StyledSuccessSnackbar>
            )}
          </Box>
        </Snackbar>
      )}
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = (): SnackbarContextType => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};
